import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes, createGlobalStyle } from 'styled-components';
import {
  Save, LogOut, Plus, Trash2, ChevronDown, ChevronUp,
  Image as ImageIcon, AlertCircle, CheckCircle, Package,
  LayoutDashboard, RotateCcw, X, ExternalLink, Loader, ShoppingBag, MapPin,
} from 'lucide-react';

/* â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
   API helper
â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';

function authHeaders() {
  const token = sessionStorage.getItem('hr_token');
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

async function apiLogin(username, password) {
  const r = await fetch(`${API}/api/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });
  const data = await r.json();
  if (!r.ok) throw new Error(data.error || 'Error de login');
  return data.token;
}

async function apiGetProducts() {
  const r = await fetch(`${API}/api/products`, { headers: authHeaders() });
  if (!r.ok) throw new Error('Error al cargar productos');
  return r.json();
}

async function apiCreateProduct(body) {
  const r = await fetch(`${API}/api/products`, {
    method: 'POST', headers: authHeaders(), body: JSON.stringify(body),
  });
  const data = await r.json();
  if (!r.ok) throw new Error(data.error || 'Error al crear');
  return data;
}

async function apiUpdateProduct(id, body) {
  const r = await fetch(`${API}/api/products/${id}`, {
    method: 'PATCH', headers: authHeaders(), body: JSON.stringify(body),
  });
  const data = await r.json();
  if (!r.ok) throw new Error(data.error || 'Error al guardar');
  return data;
}

async function apiDeleteProduct(id) {
  const r = await fetch(`${API}/api/products/${id}`, {
    method: 'DELETE', headers: authHeaders(),
  });
  if (!r.ok) throw new Error('Error al eliminar');
}

async function apiGetOrders() {
  const r = await fetch(`${API}/api/orders`, { headers: authHeaders() });
  if (!r.ok) throw new Error('Error al cargar órdenes');
  return r.json();
}

/* â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
   Global reset for admin
â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
const GlobalAdmin = createGlobalStyle`
  *, *::before, *::after { box-sizing: border-box; }
  body { margin: 0; }
`;

/* â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
   Design tokens
â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
const T = {
  bg:       '#0D0D0F',
  surface:  '#141416',
  surfaceHover: '#1A1A1E',
  border:   'rgba(255,255,255,0.07)',
  borderHover: 'rgba(255,255,255,0.14)',
  accent:   '#C07020',
  accentDim:'rgba(192,112,32,0.15)',
  text:     '#F0EEE8',
  muted:    'rgba(240,238,232,0.45)',
  danger:   '#E05252',
  dangerDim:'rgba(224,82,82,0.12)',
  green:    '#52C97A',
  greenDim: 'rgba(82,201,122,0.12)',
};

/* â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
   Animations
â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
const fadeIn  = keyframes`from{opacity:0;transform:translateY(6px);}to{opacity:1;transform:translateY(0);}`;
const slideIn = keyframes`from{opacity:0;transform:translateX(16px);}to{opacity:1;transform:translateX(0);}`;
const spin    = keyframes`to{transform:rotate(360deg)}`;

/* â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
   Layout
â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
const Shell = styled.div`
  display: flex;
  min-height: 100vh;
  background: ${T.bg};
  color: ${T.text};
  font-family: -apple-system, BlinkMacSystemFont, 'Inter', 'Segoe UI', sans-serif;
  font-size: 14px;
`;

/* Sidebar */
const Sidebar = styled.aside`
  width: 220px;
  flex-shrink: 0;
  background: ${T.surface};
  border-right: 1px solid ${T.border};
  display: flex;
  flex-direction: column;
  position: sticky;
  top: 0;
  height: 100vh;
  overflow-y: auto;
  @media (max-width: 768px) { display: none; }
`;
const SidebarLogo = styled.div`
  padding: 24px 20px 20px;
  border-bottom: 1px solid ${T.border};
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.15rem;
  font-weight: 600;
  color: ${T.accent};
  letter-spacing: .02em;
`;
const SidebarLabel = styled.div`
  padding: 20px 20px 8px;
  font-size: .68rem;
  letter-spacing: .12em;
  color: ${T.muted};
  text-transform: uppercase;
`;
const SidebarItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 20px;
  border-radius: 8px;
  margin: 2px 8px;
  cursor: pointer;
  font-size: .85rem;
  color: ${({ $active }) => $active ? T.text : T.muted};
  background: ${({ $active }) => $active ? 'rgba(255,255,255,0.06)' : 'transparent'};
  transition: background .15s, color .15s;
  &:hover { background: rgba(255,255,255,0.05); color: ${T.text}; }
`;
const SidebarBottom = styled.div`
  margin-top: auto;
  padding: 16px;
  border-top: 1px solid ${T.border};
`;

/* Main */
const Main = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
`;
const Topbar = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 32px;
  height: 56px;
  border-bottom: 1px solid ${T.border};
  background: ${T.surface};
  position: sticky;
  top: 0;
  z-index: 50;
`;
const TopLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: .82rem;
  color: ${T.muted};
`;
const Crumb = styled.span`
  color: ${({ $active }) => $active ? T.text : T.muted};
  font-weight: ${({ $active }) => $active ? 500 : 400};
`;
const CrumbSep = styled.span`color: rgba(255,255,255,.2); margin: 0 4px;`;
const TopActions = styled.div`display:flex; gap:10px; align-items:center;`;

/* Buttons */
const Btn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: ${({ $sm }) => $sm ? '6px 14px' : '8px 16px'};
  border-radius: 8px;
  font-size: .8rem;
  font-weight: 500;
  letter-spacing: .02em;
  cursor: pointer;
  transition: background .15s, border-color .15s, transform .1s;
  white-space: nowrap;
  background: ${({ $primary, $danger }) =>
    $primary ? T.accent :
    $danger  ? T.dangerDim : 'rgba(255,255,255,0.06)'};
  color: ${({ $primary, $danger }) =>
    $primary ? '#fff' :
    $danger  ? T.danger : T.text};
  border: 1px solid ${({ $primary, $danger }) =>
    $primary ? 'transparent' :
    $danger  ? 'rgba(224,82,82,.3)' : T.border};
  &:hover {
    background: ${({ $primary, $danger }) =>
      $primary ? '#A05A10' :
      $danger  ? 'rgba(224,82,82,.22)' : 'rgba(255,255,255,0.1)'};
    transform: translateY(-1px);
  }
  &:active { transform: translateY(0); }
  &:disabled { opacity: .5; cursor: not-allowed; transform: none; }
`;

/* Content area */
const Content = styled.div`
  padding: 32px;
  max-width: 1200px;
  animation: ${fadeIn} .3s ease;
`;

/* Stats row */
const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 32px;
  @media (max-width: 640px) { grid-template-columns: 1fr; }
`;
const StatCard = styled.div`
  background: ${T.surface};
  border: 1px solid ${T.border};
  border-radius: 12px;
  padding: 20px 24px;
`;
const StatValue = styled.div`
  font-size: 1.8rem;
  font-weight: 600;
  color: ${T.text};
  font-family: 'Cormorant Garamond', serif;
`;
const StatLabel = styled.div`font-size: .75rem; color: ${T.muted}; margin-top: 4px;`;
const StatBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: .68rem;
  background: ${T.accentDim};
  color: ${T.accent};
  margin-right: 4px;
`;

/* Table */
const TableWrap = styled.div`
  background: ${T.surface};
  border: 1px solid ${T.border};
  border-radius: 14px;
  overflow-x: auto;
  overflow-y: hidden;
  margin-bottom: 16px;
`;
const TableHead = styled.div`
  display: grid;
  grid-template-columns: 64px 1fr 90px 100px 90px 90px 48px;
  gap: 0;
  padding: 0 20px;
  border-bottom: 1px solid ${T.border};
  background: rgba(255,255,255,0.02);
`;
const Th = styled.div`
  padding: 12px 8px;
  font-size: .68rem;
  letter-spacing: .1em;
  color: ${T.muted};
  text-transform: uppercase;
  font-weight: 500;
`;
const TableRow = styled.div`
  display: grid;
  grid-template-columns: 64px 1fr 90px 100px 90px 90px 48px;
  gap: 0;
  padding: 0 20px;
  border-bottom: 1px solid ${T.border};
  cursor: pointer;
  transition: background .12s;
  &:last-child { border-bottom: none; }
  &:hover { background: ${T.surfaceHover}; }
  ${({ $open }) => $open ? `background: ${T.surfaceHover};` : ''}
`;
const Td = styled.div`
  padding: 14px 8px;
  display: flex;
  align-items: center;
  font-size: .85rem;
  color: ${T.text};
  min-width: 0;
`;
const Thumb = styled.div`
  width: 44px; height: 40px;
  border-radius: 8px;
  overflow: hidden;
  background: rgba(255,255,255,0.05);
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  img { width: 100%; height: 100%; object-fit: cover; }
`;
const ProductNameTd = styled.div`
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const ProductSubTd = styled.div`
  font-size: .72rem;
  color: ${T.muted};
  margin-top: 2px;
`;
const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: .68rem;
  font-weight: 500;
  letter-spacing: .04em;
  background: ${T.accentDim};
  color: ${T.accent};
  border: 1px solid rgba(192,112,32,.25);
`;
const PriceText = styled.span`
  font-variant-numeric: tabular-nums;
  color: ${({ $zero }) => $zero ? T.muted : T.text};
`;
const RowActions = styled.div`display: flex; align-items: center; gap: 4px;`;
const IconAction = styled.button`
  width: 30px; height: 30px;
  border-radius: 7px;
  display: flex; align-items: center; justify-content: center;
  background: transparent;
  border: 1px solid transparent;
  color: ${T.muted};
  cursor: pointer;
  transition: background .12s, color .12s, border-color .12s;
  &:hover {
    background: ${({ $danger }) => $danger ? T.dangerDim : 'rgba(255,255,255,0.07)'};
    color: ${({ $danger }) => $danger ? T.danger : T.text};
    border-color: ${({ $danger }) => $danger ? 'rgba(224,82,82,.25)' : T.border};
  }
  &:disabled { opacity: .4; cursor: not-allowed; }
`;
const SpinIcon = styled(Loader)`animation: ${spin} .8s linear infinite;`;

/* Edit drawer */
const Drawer = styled.div`
  background: ${T.bg};
  border-top: 1px solid ${T.border};
  padding: 28px;
  animation: ${fadeIn} .2s ease;
`;
const DrawerGrid = styled.div`
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 28px;
  @media (max-width: 700px) { grid-template-columns: 1fr; }
`;
const FieldGroup = styled.div`margin-bottom: 16px;`;
const FL = styled.label`
  display: block;
  font-size: .68rem;
  letter-spacing: .1em;
  color: ${T.muted};
  margin-bottom: 6px;
  text-transform: uppercase;
`;
const R = () => <span style={{color:'#e05252', marginLeft:2}}>*</span>;
const FI = styled.input`
  width: 100%;
  padding: 9px 12px;
  border-radius: 8px;
  font-size: .85rem;
  color: ${T.text};
  background: rgba(255,255,255,0.05);
  border: 1px solid ${T.border};
  transition: border-color .15s, background .15s;
  &:focus { outline: none; border-color: ${T.accent}; background: rgba(192,112,32,0.07); }
`;
const FTA = styled.textarea`
  width: 100%;
  padding: 9px 12px;
  border-radius: 8px;
  font-size: .85rem;
  color: ${T.text};
  background: rgba(255,255,255,0.05);
  border: 1px solid ${T.border};
  resize: vertical;
  min-height: 72px;
  line-height: 1.5;
  transition: border-color .15s, background .15s;
  &:focus { outline: none; border-color: ${T.accent}; background: rgba(192,112,32,0.07); }
`;
const DrawerMetaRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  gap: 14px;
  margin-bottom: 20px;
  @media (max-width: 700px) { grid-template-columns: 1fr 1fr; }
`;
const LangTabs = styled.div`display: grid; grid-template-columns: 1fr 1fr; gap: 20px;`;
const LangSection = styled.div`
  background: rgba(255,255,255,0.025);
  border: 1px solid ${T.border};
  border-radius: 10px;
  padding: 16px;
`;
const LangHeading = styled.div`
  font-size: .7rem;
  letter-spacing: .12em;
  color: ${T.muted};
  margin-bottom: 14px;
  text-transform: uppercase;
  display: flex;
  align-items: center;
  gap: 8px;
`;
const DrawerFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid ${T.border};
`;

/* Image uploader */
const ImgSide = styled.div``;
const ImgBox = styled.div`
  width: 100%;
  aspect-ratio: 4/3;
  border-radius: 10px;
  overflow: hidden;
  background: rgba(255,255,255,0.04);
  border: 1px solid ${T.border};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 10px;
  img { width: 100%; height: 100%; object-fit: cover; }
`;
const UploadBtn = styled.label`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  width: 100%;
  padding: 9px;
  border-radius: 8px;
  font-size: .78rem;
  font-weight: 500;
  color: ${T.accent};
  background: ${T.accentDim};
  border: 1px dashed rgba(192,112,32,.4);
  cursor: pointer;
  transition: background .15s;
  &:hover { background: rgba(192,112,32,.25); }
  input { display: none; }
`;
const UrlInput = styled(FI)`font-size:.75rem; margin-top:8px;`;

/* Add row */
const AddRow = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 14px;
  border-radius: 10px;
  background: transparent;
  border: 1px dashed ${T.border};
  color: ${T.muted};
  font-size: .82rem;
  cursor: pointer;
  transition: border-color .15s, color .15s, background .15s;
  &:hover { border-color: ${T.accent}; color: ${T.accent}; background: ${T.accentDim}; }
`;

/* Loading / Empty */
const CenterBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 64px 32px;
  color: ${T.muted};
  font-size: .85rem;
`;

/* Toast */
const Toast = styled.div`
  position: fixed;
  bottom: 28px;
  right: 28px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 13px 18px;
  border-radius: 10px;
  font-size: .82rem;
  font-weight: 500;
  z-index: 9999;
  animation: ${slideIn} .3s ease;
  background: ${({ $err }) => $err ? T.dangerDim : T.greenDim};
  border: 1px solid ${({ $err }) => $err ? 'rgba(224,82,82,.35)' : 'rgba(82,201,122,.35)'};
  color: ${({ $err }) => $err ? T.danger : T.green};
  backdrop-filter: blur(12px);
`;

/* â”€â”€ Login â”€â”€ */
const LoginShell = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${T.bg};
  background-image:
    radial-gradient(ellipse 60% 40% at 50% 0%, rgba(192,112,32,.12) 0%, transparent 70%);
`;
const LoginCard = styled.div`
  width: 380px;
  background: ${T.surface};
  border: 1px solid ${T.border};
  border-radius: 16px;
  padding: 40px 36px;
  animation: ${fadeIn} .4s ease;
`;
const LoginLogo = styled.div`
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.5rem;
  font-weight: 600;
  color: ${T.accent};
  text-align: center;
  margin-bottom: 4px;
`;
const LoginSub = styled.p`
  text-align: center;
  font-size: .7rem;
  letter-spacing: .14em;
  color: ${T.muted};
  margin: 0 0 32px;
  text-transform: uppercase;
`;
const LoginError = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 8px;
  background: ${T.dangerDim};
  border: 1px solid rgba(224,82,82,.28);
  color: ${T.danger};
  font-size: .8rem;
  margin-bottom: 16px;
`;

/* â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
   ProductRow
â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
function ProductRow({ product, open, onToggle, onSaved, onDeleted, showToast }) {
  const [draft, setDraft]       = useState(product);
  const [saving, setSaving]     = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);   // File object pendiente
  const [preview, setPreview]   = useState(null);         // blob URL para preview local
  const isNew = !product.id;

  // Sync draft when product changes from parent
  useEffect(() => { setDraft(product); }, [product]);

  // Limpiar blob URL al desmontar o al cambiar preview
  useEffect(() => {
    return () => { if (preview) URL.revokeObjectURL(preview); };
  }, [preview]);

  function set(path, value) {
    setDraft(prev => {
      const clone = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let obj = clone;
      for (let i = 0; i < keys.length - 1; i++) obj = obj[keys[i]] = obj[keys[i]] || {};
      obj[keys[keys.length - 1]] = value;
      return clone;
    });
  }

  function handleFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    // Solo mostrar preview local, NO subir todavía
    if (preview) URL.revokeObjectURL(preview);
    const blobUrl = URL.createObjectURL(file);
    setPendingFile(file);
    setPreview(blobUrl);
    // Limpiar la URL guardada para que el preview tome precedencia
    set('image', null);
    // Reset input para permitir reseleccionar el mismo archivo
    e.target.value = '';
  }

  function handleCancelImage() {
    if (preview) URL.revokeObjectURL(preview);
    setPendingFile(null);
    setPreview(null);
  }

  async function handleSave(e) {
    e.stopPropagation();

    const missing = [];
    if (!draft.v?.trim())           missing.push('Slug / Variedad');
    if (!draft.price || draft.price <= 0) missing.push('Precio USD');
    if (!draft.priceMxn || draft.priceMxn <= 0) missing.push('Precio MXN');
    if (!pendingFile && !draft.image?.trim()) missing.push('Imagen');
    if (!draft.es?.name?.trim())    missing.push('Nombre (ES)');
    if (!draft.es?.desc?.trim())    missing.push('Descripción (ES)');
    if (!draft.en?.name?.trim())    missing.push('Name (EN)');
    if (!draft.en?.desc?.trim())    missing.push('Description (EN)');
    if (!draft.weightKg || draft.weightKg <= 0) missing.push('Peso (kg)');
    if (!draft.lengthCm || draft.lengthCm <= 0) missing.push('Largo (cm)');
    if (!draft.widthCm  || draft.widthCm  <= 0) missing.push('Ancho (cm)');
    if (!draft.heightCm || draft.heightCm <= 0) missing.push('Alto (cm)');

    if (missing.length) {
      showToast(`Faltan campos: ${missing.join(', ')}`, true);
      return;
    }
    setSaving(true);
    try {
      let imageUrl = draft.image || null;

      // Si hay archivo pendiente, subirlo ahora
      if (pendingFile) {
        const token = sessionStorage.getItem('hr_token');
        const fd = new FormData();
        fd.append('image', pendingFile);
        const res = await fetch(`${API}/api/products/upload-image`, {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: fd,
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Error al subir imagen');
        imageUrl = data.url;
        // Limpiar estado de pendiente
        URL.revokeObjectURL(preview);
        setPendingFile(null);
        setPreview(null);
      }

      const body = {
        v:        draft.v,
        price:    draft.price,
        priceMxn: draft.priceMxn || null,
        image:    imageUrl,
        name_es:  draft.es?.name || '',
        desc_es:  draft.es?.desc || '',
        name_en:  draft.en?.name || '',
        desc_en:  draft.en?.desc || '',
        stock:    draft.stock ?? 0,
        active:   draft.active !== false,
        weightKg: draft.weightKg || null,
        lengthCm: draft.lengthCm || null,
        widthCm:  draft.widthCm  || null,
        heightCm: draft.heightCm || null,
      };
      let saved;
      if (isNew) {
        saved = await apiCreateProduct(body);
        showToast('Producto creado');
      } else {
        saved = await apiUpdateProduct(draft.id, body);
        showToast('Cambios guardados');
      }
      onSaved(saved);
    } catch (err) {
      showToast(err.message, true);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(e) {
    e.stopPropagation();
    if (!window.confirm('Â¿Eliminar este producto?')) return;
    setDeleting(true);
    try {
      if (!isNew) await apiDeleteProduct(draft.id);
      onDeleted();
    } catch (err) {
      showToast(err.message, true);
      setDeleting(false);
    }
  }

  return (
    <>
      <TableRow $open={open} onClick={onToggle}>
        <Td>
          <Thumb>
            {draft.image
              ? <img src={draft.image} alt="" onError={e => { e.target.style.display = 'none'; }} />
              : <ImageIcon size={16} color="rgba(255,255,255,.2)" />}
          </Thumb>
        </Td>
        <Td>
          <div>
            <ProductNameTd>{draft.es?.name || draft.en?.name || 'Nuevo producto'}</ProductNameTd>
            <ProductSubTd>{draft.en?.name}</ProductSubTd>
          </div>
        </Td>
        <Td><Badge>{draft.v || 'â€”'}</Badge></Td>
        <Td>
          <PriceText $zero={!draft.price}>
            {draft.price > 0 ? `$${Number(draft.price).toFixed(2)}` : 'â€”'}
          </PriceText>
        </Td>
        <Td style={{ color: T.muted, fontSize: '.75rem' }}>
          {draft.stock ?? 0} uds
        </Td>
        <Td style={{ color: T.muted, fontSize: '.75rem' }}>
          {draft.active !== false
            ? <span style={{ color: T.green }}>● Activo</span>
            : <span style={{ color: T.danger }}>● Inactivo</span>}
        </Td>
        <Td onClick={e => e.stopPropagation()}>
          <RowActions>
            <IconAction onClick={onToggle} title="Editar">
              {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </IconAction>
            <IconAction $danger disabled={deleting} onClick={handleDelete} title="Eliminar">
              {deleting ? <SpinIcon size={13} /> : <Trash2 size={13} />}
            </IconAction>
          </RowActions>
        </Td>
      </TableRow>

      {open && (
        <Drawer onClick={e => e.stopPropagation()}>
          <DrawerMetaRow>
            <FieldGroup style={{ margin: 0 }}>
              <FL>Slug / Variedad <R/></FL>
              <FI value={draft.v || ''} onChange={e => set('v', e.target.value)} placeholder="cajeta-natural" />
            </FieldGroup>
            <FieldGroup style={{ margin: 0 }}>
              <FL>Precio USD <R/></FL>
              <FI type="number" step="0.01" value={draft.price || ''}
                onChange={e => set('price', parseFloat(e.target.value) || 0)} />
            </FieldGroup>
            <FieldGroup style={{ margin: 0 }}>
              <FL>Precio MXN <R/></FL>
              <FI type="number" step="1" value={draft.priceMxn || ''}
                onChange={e => set('priceMxn', parseFloat(e.target.value) || null)}
                placeholder="Requerido" required />
            </FieldGroup>
            <FieldGroup style={{ margin: 0 }}>
              <FL>Stock</FL>
              <FI type="number" step="1" value={draft.stock ?? ''}
                onChange={e => set('stock', parseInt(e.target.value, 10) || 0)} />
            </FieldGroup>
            <FieldGroup style={{ margin: 0 }}>
              <FL>Peso unitario (kg) <R/></FL>
              <FI type="number" step="0.001" value={draft.weightKg ?? ''}
                onChange={e => set('weightKg', parseFloat(e.target.value) || null)}
                placeholder="ej. 0.700" />
            </FieldGroup>
          </DrawerMetaRow>
          <DrawerMetaRow style={{ gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 14 }}>
            <FieldGroup style={{ margin: 0 }}>
              <FL>Largo empaque (cm) <R/></FL>
              <FI type="number" step="0.1" value={draft.lengthCm ?? ''}
                onChange={e => set('lengthCm', parseFloat(e.target.value) || null)}
                placeholder="ej. 20" />
            </FieldGroup>
            <FieldGroup style={{ margin: 0 }}>
              <FL>Ancho empaque (cm) <R/></FL>
              <FI type="number" step="0.1" value={draft.widthCm ?? ''}
                onChange={e => set('widthCm', parseFloat(e.target.value) || null)}
                placeholder="ej. 15" />
            </FieldGroup>
            <FieldGroup style={{ margin: 0 }}>
              <FL>Alto empaque (cm) <R/></FL>
              <FI type="number" step="0.1" value={draft.heightCm ?? ''}
                onChange={e => set('heightCm', parseFloat(e.target.value) || null)}
                placeholder="ej. 10" />
            </FieldGroup>
          </DrawerMetaRow>

          <DrawerGrid>
            {/* Image column */}
            <ImgSide>
              <FL>Imagen del producto <R/></FL>
              <ImgBox>
                {(preview || draft.image)
                  ? <img src={preview || draft.image} alt="" onError={e => { e.target.style.display = 'none'; }} />
                  : <ImageIcon size={32} color="rgba(255,255,255,.15)" />}
              </ImgBox>

              {/* Badge de preview pendiente */}
              {preview && (
                <div style={{
                  display:'flex', alignItems:'center', justifyContent:'space-between',
                  background:'rgba(192,112,32,.15)', border:'1px solid rgba(192,112,32,.3)',
                  borderRadius:7, padding:'5px 10px', marginBottom:8, fontSize:'.72rem', color:'#C07020',
                }}>
                  <span>📎 Imagen lista — se subirá al guardar</span>
                  <button onClick={handleCancelImage} style={{
                    background:'none', border:'none', color:'#C07020', cursor:'pointer',
                    fontWeight:700, fontSize:'.8rem', padding:'0 0 0 8px',
                  }}>✕</button>
                </div>
              )}

              <UploadBtn>
                <input type="file" accept="image/*" onChange={handleFile} />
                <ImageIcon size={14} />
                {preview ? 'Cambiar selección' : draft.image ? 'Cambiar foto' : 'Subir foto'}
              </UploadBtn>
              <UrlInput
                placeholder="ó pega una URL"
                value={(!preview && draft.image) ? draft.image : ''}
                onChange={e => { handleCancelImage(); set('image', e.target.value); }}
              />
            </ImgSide>

            {/* Text columns */}
            <LangTabs>
              <LangSection>
                <LangHeading><span>{'\uD83C\uDDF2\uD83C\uDDFD'}</span> Español</LangHeading>
                <FieldGroup>
                  <FL>Nombre <R/></FL>
                  <FI value={draft.es?.name || ''} onChange={e => set('es.name', e.target.value)} />
                </FieldGroup>
                <FieldGroup style={{ margin: 0 }}>
                  <FL>Descripción <R/></FL>
                  <FTA value={draft.es?.desc || ''} onChange={e => set('es.desc', e.target.value)} />
                </FieldGroup>
              </LangSection>

              <LangSection>
                <LangHeading><span>{'\uD83C\uDDFA\uD83C\uDDF8'}</span> English</LangHeading>
                <FieldGroup>
                  <FL>Name <R/></FL>
                  <FI value={draft.en?.name || ''} onChange={e => set('en.name', e.target.value)} />
                </FieldGroup>
                <FieldGroup style={{ margin: 0 }}>
                  <FL>Description <R/></FL>
                  <FTA value={draft.en?.desc || ''} onChange={e => set('en.desc', e.target.value)} />
                </FieldGroup>
              </LangSection>
            </LangTabs>
          </DrawerGrid>

          <DrawerFooter>
            <Btn onClick={onToggle}>Cancelar</Btn>
            <Btn $primary disabled={saving} onClick={handleSave}>
              {saving ? <SpinIcon size={13} /> : <Save size={13} />}
              {isNew ? 'Crear producto' : 'Guardar cambios'}
            </Btn>
          </DrawerFooter>
        </Drawer>
      )}
    </>
  );
}

/* â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
   AdminPage
â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
/* ─────────────────────────────────────────────
   OrderDetailModal
───────────────────────────────────────────── */
const ModalOverlay = styled.div`
  position: fixed; inset: 0; z-index: 1000;
  background: rgba(0,0,0,0.65);
  display: flex; align-items: center; justify-content: center;
  padding: 24px;
  animation: ${fadeIn} .18s ease;
`;
const ModalBox = styled.div`
  background: ${T.surface};
  border: 1px solid ${T.border};
  border-radius: 16px;
  width: 100%; max-width: 680px;
  max-height: 85vh;
  overflow-y: auto;
  box-shadow: 0 24px 60px rgba(0,0,0,0.6);
  animation: ${fadeIn} .2s ease;
`;
const ModalHeader = styled.div`
  display: flex; align-items: center; gap: 12px;
  padding: 18px 22px;
  border-bottom: 1px solid ${T.border};
  position: sticky; top: 0; background: ${T.surface}; z-index: 1;
`;
const ModalBody = styled.div`
  padding: 20px 22px;
  display: flex; flex-direction: column; gap: 14px;
`;
const DetailCard = styled.div`
  background: ${T.bg};
  border: 1px solid ${T.border};
  border-radius: 10px;
  padding: 14px 16px;
`;
const DetailCardTitle = styled.div`
  color: ${T.muted};
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  margin-bottom: 10px;
`;
const CloseBtn = styled.button`
  margin-left: auto;
  background: none;
  border: 1px solid ${T.border};
  border-radius: 8px;
  color: ${T.muted};
  width: 32px; height: 32px;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  font-size: 18px;
  line-height: 1;
  &:hover { color: ${T.text}; border-color: ${T.borderHover}; }
`;

function OrderDetailModal({ order, onClose }) {
  if (!order) return null;
  const addr = (() => {
    if (!order.shipping_address) return {};
    try { return typeof order.shipping_address === 'string' ? JSON.parse(order.shipping_address) : order.shipping_address; }
    catch { return {}; }
  })();
  const itemList = Array.isArray(order.items) ? order.items
    : (() => { try { return JSON.parse(order.items) || []; } catch { return []; } })();
  const fmtDate = s => s ? new Date(s).toLocaleDateString('es-MX', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  }) : '—';
  const fmtTotal = o => {
    const sym = o.currency === 'mxn' ? 'MX$' : '$';
    return o.amount_total ? `${sym}${(o.amount_total / 100).toFixed(2)}` : '—';
  };

  return (
    <ModalOverlay onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <ModalBox>
        <ModalHeader>
          <span style={{ fontFamily:'monospace', fontSize:16, fontWeight:700, color: T.accent }}>
            {order.order_code || '—'}
          </span>
          <span style={{
            color: order.status === 'paid' ? T.green : T.accent,
            fontWeight: 600, fontSize: 11,
            background: order.status === 'paid' ? T.greenDim : T.accentDim,
            padding: '3px 12px', borderRadius: 20,
            border: `1px solid ${order.status === 'paid' ? T.green : T.accent}44`,
          }}>
            {order.status}
          </span>
          <span style={{ color: T.muted, fontSize: 12 }}>{fmtDate(order.created_at)}</span>
          <CloseBtn onClick={onClose}>×</CloseBtn>
        </ModalHeader>
        <ModalBody>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
            <DetailCard>
              <DetailCardTitle>Cliente</DetailCardTitle>
              <div style={{ fontWeight:600, color: T.text, marginBottom:4 }}>{addr.name || '—'}</div>
              <div style={{ color: T.muted, fontSize:12 }}>{order.customer_email || '—'}</div>
              {addr.phone && <div style={{ color: T.muted, fontSize:12, marginTop:4 }}>{addr.phone}</div>}
            </DetailCard>
            <DetailCard>
              <DetailCardTitle>Pago</DetailCardTitle>
              <div style={{ fontSize:22, fontWeight:700, color: T.accent, marginBottom:4 }}>{fmtTotal(order)}</div>
              <div style={{ color: T.muted, fontSize:11, textTransform:'uppercase' }}>{(order.currency || '').toUpperCase()}</div>
            </DetailCard>
          </div>
          <DetailCard>
            <DetailCardTitle>Dirección de envío</DetailCardTitle>
            <div style={{ color: T.text, fontSize:13, lineHeight:1.7 }}>
              {addr.line1 && <div>{addr.line1}</div>}
              {addr.line2 && <div>{addr.line2}</div>}
              <div>{[addr.city, addr.state, addr.postal_code].filter(Boolean).join(', ')}</div>
              <div style={{ color: T.muted }}>{addr.country || '—'}</div>
            </div>
          </DetailCard>
          <DetailCard>
            <DetailCardTitle>Productos ({itemList.length})</DetailCardTitle>
            {itemList.map((item, i) => (
              <div key={i} style={{
                display:'flex', alignItems:'center', gap:12, padding:'9px 0',
                borderBottom: i < itemList.length - 1 ? `1px solid ${T.border}` : 'none',
              }}>
                {item.image && (
                  <img src={item.image} alt={item.name}
                    style={{ width:40, height:40, borderRadius:8, objectFit:'cover', background:'#222' }} />
                )}
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:600, color: T.text }}>{item.name || '—'}</div>
                  {item.description && (
                    <div style={{ color: T.muted, fontSize:11, marginTop:2 }}>{item.description}</div>
                  )}
                </div>
                <div style={{ color: T.muted, fontSize:12 }}>×{item.quantity}</div>
                <div style={{ fontWeight:600, fontSize:13, minWidth:70, textAlign:'right', color: T.text }}>
                  {item.unit_amount != null
                    ? `${order.currency === 'mxn' ? 'MX$' : '$'}${(item.unit_amount / 100).toFixed(2)}`
                    : '—'}
                </div>
              </div>
            ))}
          </DetailCard>
        </ModalBody>
      </ModalBox>
    </ModalOverlay>
  );
}

/* ─────────────────────────────────────────
   OriginTab — gestión de dirección de origen
──────────────────────────────────────────── */
const OriginSection = styled.div`max-width:680px;`;
const OriginRegionTabs = styled.div`display:flex; gap:8px; margin-bottom:24px;`;
const OriginRegionBtn = styled.button`
  padding:7px 20px; border-radius:8px; font-size:.8rem; font-weight:700; cursor:pointer;
  border:1.5px solid ${({$a, theme})=> $a ? '#C07020' : 'rgba(255,255,255,.1)'};
  background:${({$a})=> $a ? 'rgba(192,112,32,.15)' : 'transparent'};
  color:${({$a})=> $a ? '#C07020' : 'rgba(255,255,255,.5)'};
  transition:all .15s;
`;
const OriginGrid = styled.div`
  display:grid; grid-template-columns:1fr 1fr; gap:14px;
  @media(max-width:600px){grid-template-columns:1fr;}
`;
const OriginFull = styled.div`grid-column:1/-1;`;
const OriginLabel = styled.label`
  display:block; font-size:.68rem; letter-spacing:.1em; text-transform:uppercase;
  color:rgba(255,255,255,.4); font-weight:600; margin-bottom:5px;
`;
const OriginInput = styled.input`
  width:100%; padding:10px 13px; border-radius:9px; font-size:.88rem;
  color:#fff; background:rgba(255,255,255,.07); border:1px solid rgba(255,255,255,.12);
  outline:none; box-sizing:border-box; transition:border-color .15s;
  &:focus{border-color:#C07020;}
  &::placeholder{color:rgba(255,255,255,.25);}
`;
const OriginNote = styled.p`
  font-size:.75rem; color:rgba(255,255,255,.35); margin:16px 0 20px; line-height:1.6;
  background:rgba(255,255,255,.04); padding:12px 14px; border-radius:8px;
  border-left:3px solid rgba(192,112,32,.5);
`;

function OriginTab({ token, showToast }) {
  const [region, setRegion] = useState('mx');
  const [forms, setForms] = useState({
    mx: { name:'', street1:'', street2:'', city:'', state:'', zip:'', country:'MX', phone:'', email:'' },
    us: { name:'', street1:'', street2:'', city:'', state:'', zip:'', country:'US', phone:'', email:'' },
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validResult, setValidResult] = useState(null); // { valid, normalized, messages, error }

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/api/shipping/origin`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => {
        setForms({
          mx: { name:'', street1:'', street2:'', city:'', state:'', zip:'', country:'MX', phone:'', email:'', ...d.mx },
          us: { name:'', street1:'', street2:'', city:'', state:'', zip:'', country:'US', phone:'', email:'', ...d.us },
        });
      })
      .catch(() => showToast('No se pudo cargar la dirección de origen.', true))
      .finally(() => setLoading(false));
  }, [token]); // eslint-disable-line

  function sf(field, val) {
    setForms(p => ({ ...p, [region]: { ...p[region], [field]: val } }));
    setValidResult(null); // limpiar resultado al editar
  }

  async function handleValidate() {
    setValidating(true); setValidResult(null);
    try {
      const r = await fetch(`${API}/api/shipping/origin/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ address: forms[region] }),
      });
      const d = await r.json();
      setValidResult(d);
    } catch {
      setValidResult({ valid: false, error: 'Error de conexión con el servidor.' });
    } finally {
      setValidating(false);
    }
  }

  async function handleSave() {
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/shipping/origin`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ region, address: forms[region] }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Error al guardar');
      showToast(`Dirección ${region.toUpperCase()} guardada ✓`);
    } catch (e) {
      showToast(e.message, true);
    } finally {
      setSaving(false);
    }
  }

  const f = forms[region];

  return (
    <OriginSection>
      <OriginNote>
        Esta es la dirección desde la que se envían los pedidos. Se usa para calcular tarifas en Shippo y generar las etiquetas de envío.
        Los cambios aplican inmediatamente pero <strong>se resetean si reinicias el servidor</strong> — actualiza también el archivo <code>server/src/lib/shippoLabel.js</code> para hacerlos permanentes.
      </OriginNote>

      <OriginRegionTabs>
        <OriginRegionBtn $a={region==='mx'} onClick={()=>setRegion('mx')}>🇲🇽 México</OriginRegionBtn>
        <OriginRegionBtn $a={region==='us'} onClick={()=>setRegion('us')}>🇺🇸 EE.UU.</OriginRegionBtn>
      </OriginRegionTabs>

      {loading ? (
        <div style={{color:'rgba(255,255,255,.4)', fontSize:'.9rem'}}>Cargando…</div>
      ) : (
        <OriginGrid>
          <OriginFull>
            <OriginLabel>Nombre / Razón social</OriginLabel>
            <OriginInput value={f.name} onChange={e=>sf('name',e.target.value)} placeholder="Hacienda Real" />
          </OriginFull>
          <OriginFull>
            <OriginLabel>Calle y número *</OriginLabel>
            <OriginInput value={f.street1} onChange={e=>sf('street1',e.target.value)} placeholder={region==='mx'?'Calle Principal 123':'123 Main St'} />
          </OriginFull>
          <OriginFull>
            <OriginLabel>Interior / Suite (opcional)</OriginLabel>
            <OriginInput value={f.street2||''} onChange={e=>sf('street2',e.target.value)} placeholder="Depto. 2B" />
          </OriginFull>
          <div>
            <OriginLabel>Ciudad *</OriginLabel>
            <OriginInput value={f.city} onChange={e=>sf('city',e.target.value)} placeholder={region==='mx'?'San Juan de los Lagos':'Los Angeles'} />
          </div>
          <div>
            <OriginLabel>Estado *</OriginLabel>
            <OriginInput value={f.state} onChange={e=>sf('state',e.target.value.toUpperCase().slice(0,5))} placeholder={region==='mx'?'JAL':'CA'} maxLength={5} />
          </div>
          <div>
            <OriginLabel>Código postal *</OriginLabel>
            <OriginInput value={f.zip} onChange={e=>sf('zip',e.target.value)} placeholder={region==='mx'?'47000':'90001'} maxLength={10} />
          </div>
          <div>
            <OriginLabel>País *</OriginLabel>
            <OriginInput value={f.country} onChange={e=>sf('country',e.target.value.toUpperCase().slice(0,2))} placeholder={region==='mx'?'MX':'US'} maxLength={2} />
          </div>
          <div>
            <OriginLabel>Teléfono</OriginLabel>
            <OriginInput value={f.phone||''} onChange={e=>sf('phone',e.target.value)} placeholder={region==='mx'?'+524491234567':'+12135550000'} />
          </div>
          <div>
            <OriginLabel>Email de contacto</OriginLabel>
            <OriginInput value={f.email||''} onChange={e=>sf('email',e.target.value)} placeholder="orders@grupohaciendareal.com" />
          </div>
          <OriginFull style={{marginTop:8, display:'flex', gap:10, flexWrap:'wrap', alignItems:'flex-start', flexDirection:'column'}}>
            {/* Botón validar */}
            <button
              onClick={handleValidate}
              disabled={validating || !f.street1 || !f.city || !f.zip}
              style={{
                padding:'10px 22px', borderRadius:10, border:'1.5px solid rgba(192,112,32,.5)',
                background:'transparent',
                color: validating||!f.street1||!f.city||!f.zip ? 'rgba(255,255,255,.25)' : '#C07020',
                fontWeight:700, fontSize:'.82rem', cursor: validating||!f.street1||!f.city||!f.zip ?'not-allowed':'pointer',
                display:'flex', alignItems:'center', gap:7,
              }}
            >
              <MapPin size={13} /> {validating ? 'Validando con Shippo…' : 'Validar dirección con Shippo'}
            </button>

            {/* Resultado validación */}
            {validResult && (
              <div style={{
                padding:'12px 16px', borderRadius:10, width:'100%', boxSizing:'border-box',
                background: validResult.valid ? 'rgba(16,185,129,.1)' : 'rgba(239,68,68,.1)',
                border: `1px solid ${validResult.valid ? 'rgba(16,185,129,.3)' : 'rgba(239,68,68,.3)'}`,
                fontSize:'.82rem', lineHeight:1.6,
              }}>
                <div style={{fontWeight:700, marginBottom:4, color: validResult.valid ? '#34d399' : '#f87171'}}>
                  {validResult.valid ? '✅ Dirección válida en Shippo' : '❌ Dirección con problemas'}
                </div>
                {validResult.valid && validResult.normalized && (
                  <div style={{color:'rgba(255,255,255,.55)'}}>
                    {[validResult.normalized.street1, validResult.normalized.street2,
                      validResult.normalized.city, validResult.normalized.state,
                      validResult.normalized.zip, validResult.normalized.country,
                    ].filter(Boolean).join(', ')}
                  </div>
                )}
                {validResult.messages?.length > 0 && (
                  <ul style={{margin:'6px 0 0', paddingLeft:16, color:'rgba(255,255,255,.45)'}}>
                    {validResult.messages.map((m,i) => <li key={i}>{m}</li>)}
                  </ul>
                )}
                {validResult.error && (
                  <div style={{color:'#f87171'}}>{validResult.error}</div>
                )}
              </div>
            )}

            {/* Botón guardar */}
            <button
              onClick={handleSave}
              disabled={saving || !f.street1 || !f.city || !f.zip}
              style={{
                padding:'11px 28px', borderRadius:10, border:'none',
                background: saving||!f.street1||!f.city||!f.zip ? 'rgba(255,255,255,.1)' : '#C07020',
                color: saving||!f.street1||!f.city||!f.zip ? 'rgba(255,255,255,.3)' : '#fff',
                fontWeight:700, fontSize:'.88rem', cursor: saving ? 'default':'pointer',
                display:'flex', alignItems:'center', gap:8,
              }}
            >
              <Save size={14} /> {saving ? 'Guardando…' : `Guardar dirección ${region.toUpperCase()}`}
            </button>
          </OriginFull>
        </OriginGrid>
      )}
    </OriginSection>
  );
}

export default function AdminPage() {
  const [authed,   setAuthed]   = useState(() => !!sessionStorage.getItem('hr_token'));
  const [user,     setUser]     = useState('');
  const [pass,     setPass]     = useState('');
  const [loginErr, setLoginErr] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  const [products,  setProducts]  = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [openIdx,   setOpenIdx]   = useState(null);
  const [toast,     setToast]     = useState(null);
  const [activeTab, setActiveTab] = useState('products'); // 'products' | 'orders' | 'origin'
  const [orders,    setOrders]    = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [orderFilter,   setOrderFilter]   = useState('all'); // 'all' | 'us' | 'mx'
  const [orderSearch,   setOrderSearch]   = useState('');
  const [dateFrom,      setDateFrom]      = useState('');
  const [dateTo,        setDateTo]        = useState('');
  const [expandedOrder, setExpandedOrder] = useState(null); // full order object for modal

  function showToast(msg, err = false) {
    setToast({ msg, err });
    setTimeout(() => setToast(null), 3200);
  }

  /* â”€â”€ Cargar productos desde MySQL â”€â”€ */
  const loadProducts = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiGetProducts();
      setProducts(data);
    } catch (err) {
      showToast('No se pudieron cargar los productos: ' + err.message, true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (authed) loadProducts(); }, [authed, loadProducts]);

  /* ── Cargar órdenes desde MySQL ── */
  const loadOrders = useCallback(async () => {
    setOrdersLoading(true);
    try {
      const data = await apiGetOrders();
      setOrders(data);
    } catch (err) {
      showToast('No se pudieron cargar las órdenes: ' + err.message, true);
    } finally {
      setOrdersLoading(false);
    }
  }, []);

  useEffect(() => { if (authed && activeTab === 'orders') loadOrders(); }, [authed, activeTab, loadOrders]);

  /* â”€â”€ Login contra API â”€â”€ */
  async function handleLogin(e) {
    e.preventDefault();
    setLoginLoading(true);
    setLoginErr('');
    try {
      const token = await apiLogin(user, pass);
      sessionStorage.setItem('hr_token', token);
      setAuthed(true);
    } catch (err) {
      setLoginErr(err.message || 'Usuario o contraseña incorrectos.');
    } finally {
      setLoginLoading(false);
    }
  }

  function handleLogout() {
    sessionStorage.removeItem('hr_token');
    setAuthed(false);
    setUser(''); setPass('');
    setProducts([]);
  }

  function handleProductSaved(saved) {
    setProducts(prev => {
      const idx = prev.findIndex(p => p.id === saved.id);
      if (idx === -1) return [...prev, saved];      // nuevo
      return prev.map((p, i) => i === idx ? saved : p); // actualizado
    });
    setOpenIdx(null);
  }

  function handleProductDeleted(idx) {
    setProducts(prev => prev.filter((_, i) => i !== idx));
    setOpenIdx(null);
  }

  function addProduct() {
    const newProduct = {
      id: null, v: '', price: 0, priceMxn: null, image: '', stock: 0, active: true,
      weightKg: null, lengthCm: null, widthCm: null, heightCm: null,
      en: { name: '', desc: '' },
      es: { name: '', desc: '' },
    };
    setProducts(prev => [...prev, newProduct]);
    setOpenIdx(products.length);
  }

  const activeCount = products.filter(p => p.active !== false).length;
  const avgPrice    = products.filter(p => p.price > 0)
    .reduce((a, p, _, arr) => a + p.price / arr.length, 0);
  const totalStock  = products.reduce((a, p) => a + (p.stock || 0), 0);

  /* â”€â”€ Login screen â”€â”€ */
  if (!authed) return (
    <>
      <GlobalAdmin />
      <LoginShell>
        <LoginCard>
          <LoginLogo>Hacienda Real</LoginLogo>
          <LoginSub>Panel de Administración</LoginSub>
          <form onSubmit={handleLogin}>
            {loginErr && (
              <LoginError><AlertCircle size={14} />{loginErr}</LoginError>
            )}
            <FieldGroup>
              <FL>Usuario</FL>
              <FI type="text" autoComplete="username" value={user}
                onChange={e => setUser(e.target.value)} placeholder="admin" />
            </FieldGroup>
            <FieldGroup>
              <FL>Contraseña</FL>
              <FI type="password" autoComplete="current-password" value={pass}
                onChange={e => setPass(e.target.value)} placeholder="â€¢â€¢â€¢â€¢â€¢â€¢â€¢â€¢" />
            </FieldGroup>
            <Btn $primary disabled={loginLoading}
              style={{ width: '100%', justifyContent: 'center', marginTop: 8, padding: '11px' }}
              type="submit">
              {loginLoading ? <SpinIcon size={15} /> : null}
              {loginLoading ? 'Verificandoâ€¦' : 'Entrar al panel'}
            </Btn>
          </form>
        </LoginCard>
      </LoginShell>
    </>
  );

  /* â”€â”€ Dashboard â”€â”€ */
  return (
    <>
      <GlobalAdmin />
      <Shell>
        {/* Sidebar */}
        <Sidebar>
          <SidebarLogo>Hacienda Real</SidebarLogo>
          <SidebarLabel>Menú</SidebarLabel>
          <SidebarItem $active={activeTab === 'products'} onClick={() => setActiveTab('products')}>
            <Package size={15} /> Productos
          </SidebarItem>
          <SidebarItem $active={activeTab === 'orders'} onClick={() => setActiveTab('orders')}>
            <ShoppingBag size={15} /> Órdenes
          </SidebarItem>
          <SidebarItem $active={activeTab === 'origin'} onClick={() => setActiveTab('origin')}>
            <MapPin size={15} /> Origen de envío
          </SidebarItem>
          <SidebarItem onClick={() => window.open('/', '_blank')}>
            <ExternalLink size={15} /> Ver sitio
          </SidebarItem>
          <SidebarBottom>
            <SidebarItem onClick={handleLogout} style={{ margin: 0 }}>
              <LogOut size={15} /> Cerrar sesión
            </SidebarItem>
          </SidebarBottom>
        </Sidebar>

        {/* Main */}
        <Main>
          <Topbar>
            <TopLeft>
              <Crumb>Admin</Crumb>
              <CrumbSep>/</CrumbSep>
              <Crumb $active>{activeTab === 'orders' ? 'Órdenes' : activeTab === 'origin' ? 'Origen de envío' : 'Productos'}</Crumb>
              {(loading || ordersLoading) && <SpinIcon size={13} style={{ marginLeft: 6, color: T.accent }} />}
            </TopLeft>
            <TopActions>
              {activeTab === 'products' ? (
                <>
                  <Btn onClick={loadProducts} disabled={loading}>
                    <RotateCcw size={13} /> Recargar
                  </Btn>
                  <Btn $primary onClick={addProduct} disabled={loading}>
                    <Plus size={13} /> Nuevo producto
                  </Btn>
                </>
              ) : activeTab === 'orders' ? (
                <Btn onClick={loadOrders} disabled={ordersLoading}>
                  <RotateCcw size={13} /> Recargar
                </Btn>
              ) : null}
              <Btn $danger onClick={handleLogout}>
                <LogOut size={13} />
              </Btn>
            </TopActions>
          </Topbar>

          <Content>
            {activeTab === 'products' && (<>
            {/* Stats */}
            <StatsRow>
              <StatCard>
                <StatValue>{products.length}</StatValue>
                <StatLabel>Productos totales · <span style={{ color: T.green }}>{activeCount} activos</span></StatLabel>
              </StatCard>
              <StatCard>
                <StatValue>${avgPrice > 0 ? avgPrice.toFixed(2) : 'â€”'}</StatValue>
                <StatLabel>Precio promedio (USD)</StatLabel>
              </StatCard>
              <StatCard>
                <StatValue>{totalStock}</StatValue>
                <StatLabel>Unidades en stock</StatLabel>
              </StatCard>
            </StatsRow>

            {/* Table */}
            <TableWrap>
              <TableHead>
                <Th></Th>
                <Th>Producto</Th>
                <Th>Slug</Th>
                <Th>Precio USD</Th>
                <Th>Stock</Th>
                <Th>Estado</Th>
                <Th></Th>
              </TableHead>

              {loading && (
                <CenterBox>
                  <SpinIcon size={22} style={{ color: T.accent }} />
                  Cargando productosâ€¦
                </CenterBox>
              )}

              {!loading && products.length === 0 && (
                <CenterBox>
                  <Package size={28} color={T.muted} />
                  Sin productos. Crea el primero.
                </CenterBox>
              )}

              {!loading && products.map((product, idx) => (
                <ProductRow
                  key={product.id ?? `new-${idx}`}
                  product={product}
                  open={openIdx === idx}
                  onToggle={() => setOpenIdx(openIdx === idx ? null : idx)}
                  onSaved={handleProductSaved}
                  onDeleted={() => handleProductDeleted(idx)}
                  showToast={showToast}
                />
              ))}
            </TableWrap>

            <AddRow onClick={addProduct}>
              <Plus size={15} /> Agregar producto
            </AddRow>
            </>)}

            {activeTab === 'orders' && (<>
            {(() => {
              const mxOrders  = orders.filter(o => o.currency === 'mxn');
              const usOrders  = orders.filter(o => o.currency !== 'mxn');

              let displayed = orderFilter === 'mx' ? mxOrders
                            : orderFilter === 'us' ? usOrders
                            : orders;

              if (orderSearch.trim()) {
                const q = orderSearch.trim().toLowerCase();
                displayed = displayed.filter(o =>
                  (o.order_code && o.order_code.toLowerCase().includes(q)) ||
                  (o.customer_email && o.customer_email.toLowerCase().includes(q))
                );
              }
              if (dateFrom) {
                const from = new Date(dateFrom);
                displayed = displayed.filter(o => o.created_at && new Date(o.created_at) >= from);
              }
              if (dateTo) {
                const to = new Date(dateTo); to.setHours(23, 59, 59, 999);
                displayed = displayed.filter(o => o.created_at && new Date(o.created_at) <= to);
              }

              const fmtDate  = s => s ? new Date(s).toLocaleDateString('es-MX', { day:'2-digit', month:'short', year:'numeric' }) : '—';
              const fmtAddr  = a => {
                if (!a) return '—';
                const j = typeof a === 'string' ? (() => { try { return JSON.parse(a); } catch { return {}; } })() : a;
                return [j.line1, j.city, j.state].filter(Boolean).join(', ') || '—';
              };
              const getName  = a => {
                if (!a) return '—';
                const j = typeof a === 'string' ? (() => { try { return JSON.parse(a); } catch { return {}; } })() : a;
                return j.name || '—';
              };
              const fmtTotal = o => {
                const sym = o.currency === 'mxn' ? 'MX$' : '$';
                return o.amount_total ? `${sym}${(o.amount_total / 100).toFixed(2)}` : '—';
              };

              // 6 columns: Fecha | Código | Nombre | Dirección | Total | Estado
              const cols = '110px 120px minmax(130px,1fr) minmax(160px,1.8fr) 90px 80px';

              const filterBtnStyle = active => ({
                padding: '5px 14px', borderRadius: 6,
                border: `1px solid ${active ? T.accent : T.border}`,
                background: active ? T.accent + '22' : 'transparent',
                color: active ? T.accent : T.muted,
                cursor: 'pointer', fontSize: 12, fontWeight: active ? 600 : 400,
              });
              const inputStyle = {
                background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6,
                color: T.text, padding: '5px 10px', fontSize: 12, outline: 'none', colorScheme: 'dark',
              };

              return (
                <>
                  {/* Stats */}
                  <StatsRow>
                    <StatCard><StatValue>{orders.length}</StatValue><StatLabel>Órdenes totales</StatLabel></StatCard>
                    <StatCard><StatValue style={{ color:'#4ade80' }}>{usOrders.length}</StatValue><StatLabel>🇺🇸 USD</StatLabel></StatCard>
                    <StatCard><StatValue style={{ color:'#f87171' }}>{mxOrders.length}</StatValue><StatLabel>🇲🇽 MXN</StatLabel></StatCard>
                  </StatsRow>

                  {/* Filtros moneda */}
                  <div style={{ display:'flex', gap:8, margin:'20px 0 8px', alignItems:'center', flexWrap:'wrap' }}>
                    <span style={{ color: T.muted, fontSize:12 }}>Moneda:</span>
                    <button style={filterBtnStyle(orderFilter === 'all')} onClick={() => setOrderFilter('all')}>Todas ({orders.length})</button>
                    <button style={filterBtnStyle(orderFilter === 'us')}  onClick={() => setOrderFilter('us')}>🇺🇸 USA ({usOrders.length})</button>
                    <button style={filterBtnStyle(orderFilter === 'mx')}  onClick={() => setOrderFilter('mx')}>🇲🇽 MX ({mxOrders.length})</button>
                  </div>

                  {/* Búsqueda + fechas */}
                  <div style={{ display:'flex', gap:10, marginBottom:14, alignItems:'center', flexWrap:'wrap' }}>
                    <input style={{ ...inputStyle, width:200 }} placeholder="Buscar código o email…" value={orderSearch} onChange={e => setOrderSearch(e.target.value)} />
                    <span style={{ color: T.muted, fontSize:12 }}>Desde:</span>
                    <input type="date" style={{ ...inputStyle, width:140 }} value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
                    <span style={{ color: T.muted, fontSize:12 }}>Hasta:</span>
                    <input type="date" style={{ ...inputStyle, width:140 }} value={dateTo} onChange={e => setDateTo(e.target.value)} />
                    {(orderSearch || dateFrom || dateTo) && (
                      <button style={{ ...filterBtnStyle(false), color:'#f87171', borderColor:'#f87171' }}
                        onClick={() => { setOrderSearch(''); setDateFrom(''); setDateTo(''); }}>✕ Limpiar</button>
                    )}
                    <span style={{ color: T.muted, fontSize:11, marginLeft:'auto' }}>
                      {displayed.length} resultado{displayed.length !== 1 ? 's' : ''}
                    </span>
                  </div>

                  {/* Tabla */}
                  <TableWrap>
                    {/* Cabecera */}
                    <div style={{ display:'grid', gridTemplateColumns: cols, gap:12, padding:'8px 16px', borderBottom:`1px solid ${T.border}`, background: T.surface }}>
                      {['Fecha','Código','Nombre','Dirección','Total','Estado'].map(h => (
                        <Th key={h}>{h}</Th>
                      ))}
                    </div>

                    {ordersLoading && (
                      <CenterBox><SpinIcon size={22} style={{ color: T.accent }} /> Cargando…</CenterBox>
                    )}
                    {!ordersLoading && displayed.length === 0 && (
                      <CenterBox><ShoppingBag size={28} color={T.muted} /> Sin órdenes.</CenterBox>
                    )}
                    {!ordersLoading && displayed.map(o => (
                      <div
                        key={o.id}
                        onClick={() => setExpandedOrder(o)}
                        style={{ display:'grid', gridTemplateColumns: cols, alignItems:'center', gap:12, padding:'11px 16px', borderBottom:`1px solid ${T.border}`, fontSize:12, color: T.text, cursor:'pointer', transition:'background .15s' }}
                        onMouseEnter={e => e.currentTarget.style.background = T.surfaceHover}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <span style={{ color: T.muted, fontSize:11 }}>{fmtDate(o.created_at)}</span>
                        <span style={{ color: T.accent, fontFamily:'monospace', fontSize:11, fontWeight:700 }}>{o.order_code || '—'}</span>
                        <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{getName(o.shipping_address)}</span>
                        <span style={{ color: T.muted, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{fmtAddr(o.shipping_address)}</span>
                        <span style={{ fontWeight:600 }}>{fmtTotal(o)}</span>
                        <span style={{ color: o.status === 'paid' ? T.green : T.accent, fontWeight:600, fontSize:11, textTransform:'capitalize' }}>{o.status}</span>
                      </div>
                    ))}
                  </TableWrap>

                  {/* Modal detalle */}
                  <OrderDetailModal order={expandedOrder} onClose={() => setExpandedOrder(null)} />
                </>
              );
            })()}
            </>)}

            {activeTab === 'origin' && (
              <OriginTab token={sessionStorage.getItem('hr_token')} showToast={showToast} />
            )}
          </Content>
        </Main>
      </Shell>

      {toast && (
        <Toast $err={toast.err}>
          {toast.err ? <AlertCircle size={15} /> : <CheckCircle size={15} />}
          {toast.msg}
        </Toast>
      )}
    </>
  );
}
